class NumberUtils {
  static formatCurrency = (
    figure: number,
    options?: Record<string, unknown>
  ) => {
    const { space = true } = options ?? {};

    if (figure < 1000) {
      return figure;
    } else if (figure < 100000) {
      const formatted = (figure / 1000).toFixed(1);
      return formatted.endsWith('.0')
        ? formatted.slice(0, -2) + `${space ? ' ' : ''}k`
        : formatted + `${space ? ' ' : ''}k`;
    } else if (figure < 10000000) {
      const formatted = (figure / 100000).toFixed(1);
      return formatted.endsWith('.0')
        ? formatted.slice(0, -2) + `${space ? ' ' : ''}L`
        : formatted + `${space ? ' ' : ''}L`;
    } else {
      const formatted = (figure / 10000000).toFixed(1);
      return formatted.endsWith('.0')
        ? formatted.slice(0, -2) + `${space ? ' ' : ''}cr`
        : formatted + `${space ? ' ' : ''}cr`;
    }
  };

  // Keeps digits and a single decimal point, e.g. "2a3..4" -> "23.4"
  static sanitizeDecimal = (value: string) => {
    const [whole, ...decimals] = (value ?? '')
      .replace(/[^0-9.]/g, '')
      .split('.');
    return decimals.length > 0 ? `${whole}.${decimals.join('')}` : whole;
  };

  /**
   * Mask for a decimal range field such as interest range: either a single
   * value ("23.45") or a min - max pair ("23.45 - 34.89"). A hyphen or a
   * space starts the second value and is shown as " - " once it has digits.
   */
  static sanitizeRangeInput = (value: string) => {
    const cleaned = (value ?? '').replace(/[^0-9.\s-]/g, '');
    const sepIndex = cleaned.search(/[\s-]/);
    if (sepIndex === -1) return NumberUtils.sanitizeDecimal(cleaned);

    const min = NumberUtils.sanitizeDecimal(cleaned.slice(0, sepIndex));
    const max = NumberUtils.sanitizeDecimal(cleaned.slice(sepIndex + 1));
    if (min === '') return max;
    // keep the separator compact until the max has digits, so it stays erasable
    return max === '' ? `${min}-` : `${min} - ${max}`;
  };

  // "23.45 - 34.89" -> { min: 23.45, max: 34.89 }, "23.45" -> { min: 23.45, max: null }
  static parseRange = (value: string) => {
    const parts = (value ?? '')
      .split(/[\s-]+/)
      .map(part => parseFloat(part))
      .filter(part => !Number.isNaN(part));
    return {
      min: parts.length > 0 ? parts[0] : null,
      max: parts.length > 1 ? parts[1] : null,
    };
  };

  static formatRange = (value: string) => {
    const { min, max } = NumberUtils.parseRange(value);
    if (min === null) return '';
    return max === null ? `${min}` : `${min} - ${max}`;
  };
}

export default NumberUtils;
